import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, Navigate } from 'react-router-dom';
import {
  SessionType,
  logRocketIdentifyAdvisorUser,
  logRocketIdentifyClientUser,
} from '@shared';
import { analytics, posthogIdentifyUser } from '@shared/posthog';
import { FullScreenLoadingOverlay } from './FullScreenLoadingOverlay';
import type { ChildrenProps } from '../types/ChildrenProps';
import {
  closeSession,
  fetchSessionForAuthRequest,
  selectSession,
} from '../model';
import { DuplicateAccountScreen } from '@client/pages/DuplicateAccountScreen';
import { getPublicRoutePaths } from '../Router';
import { setAuthToken, clearAuthToken } from '@client/api/queryClient';
import { useClientRoutes } from '../hooks/useClientRoutes';
import { LoginRedirect } from './LoginRedirect';
import { getStoredImpersonationTarget, initImpersonation } from '@client/model/impersonationModel';
import { useClerkClientAuth } from '@client/hooks/useClerkClientAuth';
import {
  clearRoleOverride,
  clearRoleOverrideForDifferentUser,
} from '@client/hooks/useRoleSwitch';
import { SignIn } from '@/pages';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import {
  CLIENT_SMS_MFA_ENROLLMENT_PATH,
  useClientSmsMfaStatus,
} from '@/features/client-sms-mfa/use-client-sms-mfa';

const POSTHOG_SIGN_IN_PENDING_KEY = 'posthog-sign-in-pending';
let observedSignedOutInMemory = false;

function markSignInPending() {
  observedSignedOutInMemory = true;
  try {
    localStorage.setItem(POSTHOG_SIGN_IN_PENDING_KEY, 'true');
  } catch {
    // The in-memory marker still covers same-tab sign-ins when storage is unavailable.
  }
}

function consumeSignInPending(): boolean {
  const inMemoryPending = observedSignedOutInMemory;
  observedSignedOutInMemory = false;

  try {
    const isPending = localStorage.getItem(POSTHOG_SIGN_IN_PENDING_KEY) === 'true';
    localStorage.removeItem(POSTHOG_SIGN_IN_PENDING_KEY);
    return isPending;
  } catch {
    return inMemoryPending;
  }
}

export function SessionLoader({ children }: ChildrenProps) {
  const session = useSelector(selectSession);
  const dispatch = useDispatch();
  const location = useLocation();
  const { isClerkSignedIn, isClerkLoading, clerkEmail, clerkUserId, getClerkToken, clerkSignOut } =
    useClerkClientAuth();
  const { isPublicOnboardingRoute } = useClientRoutes();
  const { flags, isLoaded: areFeatureFlagsLoaded } = useFeatureFlags();
  const sessionFetchedRef = useRef(false);
  const shouldCheckClientSmsMfa =
    isClerkSignedIn &&
    session.sessionType === SessionType.client &&
    areFeatureFlagsLoaded &&
    flags.consumerSmsMfa === true;
  const clientSmsMfaStatus = useClientSmsMfaStatus(shouldCheckClientSmsMfa);

  const publicRoutes = getPublicRoutePaths();
  const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));
  const isAuthEntryRoute = location.pathname === '/' || location.pathname === '/signup';

  // Single effect: when Clerk auth state resolves, fetch or clear the server session once
  useEffect(() => {
    if (isClerkLoading) return;

    if (isClerkSignedIn && clerkUserId && !sessionFetchedRef.current) {
      sessionFetchedRef.current = true;
      clearRoleOverrideForDifferentUser(clerkUserId);
      (async () => {
        try {
          const accessToken = await getClerkToken();
          if (!accessToken) {
            clearAuthToken();
            dispatch(closeSession());
            return;
          }
          setAuthToken(accessToken);

          const storedTarget = getStoredImpersonationTarget();
          if (storedTarget) {
            dispatch(initImpersonation(storedTarget));
          }

          dispatch(fetchSessionForAuthRequest());
        } catch {
          clearAuthToken();
          dispatch(closeSession());
        }
      })();
    } else if (!isClerkSignedIn) {
      markSignInPending();
      sessionFetchedRef.current = false;
      clearRoleOverride();
      clearAuthToken();
      dispatch(closeSession());
    }
  }, [isClerkLoading, isClerkSignedIn, clerkUserId]);

  // Token refresh — Clerk tokens expire after ~60s
  useEffect(() => {
    if (!isClerkSignedIn) return;
    const id = setInterval(async () => {
      try {
        const token = await getClerkToken();
        if (token) setAuthToken(token);
      } catch { /* next API call will handle */ }
    }, 50_000);
    return () => clearInterval(id);
  }, [isClerkSignedIn]);

  // Analytics identification and successful sign-in tracking
  useEffect(() => {
    if (isClerkLoading || !isClerkSignedIn) return;
    if (session.sessionType === SessionType.client && clerkUserId) {
      if (clerkEmail) logRocketIdentifyClientUser(clerkUserId, clerkEmail);
      posthogIdentifyUser(clerkUserId, clerkEmail ? { email: clerkEmail } : {});
      if (consumeSignInPending()) analytics.auth.userSignedIn();
    } else if (session.advisor?.advisorId) {
      const isWizardImpersonating =
        session.sessionType === SessionType.wizard && session.impersonation?.isImpersonating;
      const advisorId = isWizardImpersonating
        ? session.impersonation?.wizardAdvisorId
        : session.advisor.advisorId;
      const advisorName = isWizardImpersonating
        ? session.impersonation?.wizardAdvisorFirstName || session.impersonation?.wizardEmail || 'Wizard'
        : session.advisor.advisorFirstName;
      const advisorEmail = isWizardImpersonating
        ? session.impersonation?.wizardEmail || ''
        : session.advisor.advisorEmail;
      if (advisorId) {
        logRocketIdentifyAdvisorUser(advisorId, advisorName);
        posthogIdentifyUser(advisorId, { name: advisorName, email: advisorEmail });
        if (consumeSignInPending()) analytics.auth.userSignedIn();
      }
    }
  }, [
    session.sessionType,
    session.advisor,
    isClerkLoading,
    isClerkSignedIn,
    clerkUserId,
    clerkEmail,
  ]);

  // --- Render gates (order matters) ---

  // 1. Clerk still loading — public content may render immediately, but auth entry screens must
  // wait. Otherwise an already-signed-in user can briefly see sign-up while Clerk restores them.
  if (isClerkLoading) {
    return (isPublicRoute || isPublicOnboardingRoute) && !isAuthEntryRoute
      ? children
      : <FullScreenLoadingOverlay isLoading />;
  }

  // 2. Signed in but server session still loading
  if (isClerkSignedIn && (session.sessionType === SessionType.unknown || session.isLoading)) {
    return (isPublicRoute || isPublicOnboardingRoute) && !isAuthEntryRoute
      ? children
      : <FullScreenLoadingOverlay isLoading />;
  }

  if (isClerkSignedIn && session.sessionType === SessionType.client && !areFeatureFlagsLoaded) {
    return <FullScreenLoadingOverlay isLoading />;
  }

  if (shouldCheckClientSmsMfa) {
    if (clientSmsMfaStatus.isPending) return <FullScreenLoadingOverlay isLoading />;

    if (!clientSmsMfaStatus.data?.complete) {
      if (location.pathname !== CLIENT_SMS_MFA_ENROLLMENT_PATH) {
        const redirectTo = `${location.pathname}${location.search}`;
        return (
          <Navigate
            to={`${CLIENT_SMS_MFA_ENROLLMENT_PATH}?redirect_to=${encodeURIComponent(redirectTo)}`}
            replace
          />
        );
      }
    } else if (location.pathname === CLIENT_SMS_MFA_ENROLLMENT_PATH) {
      const redirectTo = new URLSearchParams(location.search).get('redirect_to');
      return <Navigate to={redirectTo || '/client/home'} replace />;
    }
  } else if (
    isClerkSignedIn &&
    session.sessionType === SessionType.client &&
    location.pathname === CLIENT_SMS_MFA_ENROLLMENT_PATH
  ) {
    return <Navigate to="/client/home" replace />;
  }

  // 3. Duplicate account
  if (session.sessionType === SessionType.duplicateAccount) {
    return <DuplicateAccountScreen />;
  }

  // 4. Signed into Clerk but server rejected — show error (not login, to avoid loop)
  if (isClerkSignedIn && session.sessionType === SessionType.unauthenticated) {
    if (isPublicRoute || isPublicOnboardingRoute) return children;
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-2xl border bg-white p-8 text-center">
          <h1 className="text-xl font-bold text-gray-950">Unable to load your session</h1>
          <p className="mt-2 text-sm text-gray-600">
            You are signed in but the server could not verify your session. This is usually a temporary issue.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <button
              onClick={() => window.location.reload()}
              className="rounded-lg bg-primary px-4 py-3 text-sm font-medium text-white hover:bg-primary-hover"
            >
              Try again
            </button>
            <button
              onClick={async () => {
                clearRoleOverride();
                dispatch(closeSession());
                clearAuthToken();
                await clerkSignOut({ redirectUrl: '/' });
              }}
              className="rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Sign out and start over
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 5. Signed in and authenticated, but on an auth entry route — send home without mounting
  // Clerk's sign-in/sign-up components and triggering another redirect cycle.
  if (isClerkSignedIn && isAuthEntryRoute) {
    const searchParams = new URLSearchParams(location.search);
    const redirectTo = searchParams.get('redirect_to');
    if (session.sessionType === SessionType.client) {
      return <Navigate to={redirectTo || '/client/home'} replace />;
    }
    if (
      (session.sessionType === SessionType.advisor || session.sessionType === SessionType.wizard) &&
      session.advisor?.advisorId
    ) {
      return <Navigate to={redirectTo || `/advisors/${session.advisor.advisorId}`} replace />;
    }
  }

  // 6. Not signed in — show login
  if (!isClerkSignedIn) {
    if (isPublicRoute || isPublicOnboardingRoute) return children;
    if (location.pathname === '/') {
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.get('screen_hint') === 'signup') {
        return <Navigate to={`/signup${location.search}`} replace />;
      }
      return <SignIn />;
    }
    return <LoginRedirect />;
  }

  // 6. Authenticated — render app
  return children;
}
